/**
 * 検査実施ページ
 *
 * 線分二等分課題の検査を実施する全画面Canvas
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ExamPrepareState } from '../types';
const CANVAS_CONFIG = {
  BACKGROUND_COLOR: '#ffffff',
  LINE_COLOR: '#000000',
  MARK_COLOR: '#ff0000',
  MARK_THICKNESS: 2,
  MARK_LENGTH: 30,
} as const;
import { useExam } from '../hooks/useExam';
import {
  drawLine,
  drawMarker,
  clearCanvas,
  getCanvasCoordinates,
  findNearestLine,
} from '../utils/canvas';
import { mmToPx, getCalibration } from '../utils/calibration';

/**
 * 検査実施ページコンポーネント
 */
export default function ExamRunPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // Canvas関連
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [markedPosition, setMarkedPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  // 自動遷移時の線非表示フラグ（1秒間canvasを空にする）
  const [lineHidden, setLineHidden] = useState(false);
  const capturedImagesRef = useRef<Map<number, string>>(new Map());

  // 検査フック
  const {
    state: examState,
    initializeExam,
    startExam,
    recordMark,
    recordMultiLineMark,
    nextTrial,
    cancelExam,
    getResultSummary,
    progress,
    isCompleted,
    isCalibrated,
    isMultiLineMode,
  } = useExam();

  // 初期化済みフラグ
  const [isInitialized, setIsInitialized] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  /**
   * 検査設定を取得して初期化
   */
  useEffect(() => {
    const prepareState = location.state as ExamPrepareState | undefined;

    if (!prepareState?.patient || !prepareState?.settings) {
      // sessionStorage からも取得を試みる
      const stored = sessionStorage.getItem('examPrepareState');
      if (stored) {
        try {
          const storedState = JSON.parse(stored) as ExamPrepareState;
          initializeExam(storedState.patient, storedState.settings);
          setIsInitialized(true);
        } catch {
          sessionStorage.removeItem('examPrepareState');
          navigate('/exam/prepare');
        }
      } else {
        navigate('/exam/prepare');
      }
    } else {
      initializeExam(prepareState.patient, prepareState.settings);
      setIsInitialized(true);
    }
  }, [location.state, navigate, initializeExam]);

  /**
   * Canvas サイズを設定（全画面）
   * isInitializedに依存: 初期化後にcontainerRefがDOMに存在するようになるため
   */
  useEffect(() => {
    const updateCanvasSize = () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        setCanvasSize({ width: clientWidth, height: clientHeight });
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, [isInitialized]);

  /**
   * Canvas サイズが確定したら検査を開始
   */
  useEffect(() => {
    if (
      isInitialized &&
      canvasSize.width > 0 &&
      canvasSize.height > 0 &&
      examState.phase === 'preparing'
    ) {
      startExam(canvasSize.width, canvasSize.height);
    }
  }, [isInitialized, canvasSize, examState.phase, startExam]);

  /** マーク済み線分の色（薄いグレー） */
  const MARKED_LINE_COLOR = '#cccccc';

  /**
   * Canvasを描画
   */
  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 背景をクリア
    clearCanvas(ctx, canvas.width, canvas.height);

    // 自動遷移の線非表示期間中は白画面のまま
    if (lineHidden) return;

    if (isMultiLineMode && examState.multiLines) {
      // 複数線分モード
      const calibration = getCalibration();
      const markLengthPx = mmToPx(15, calibration.ppmm);
      const lineThicknessPx = mmToPx(
        examState.settings.lineThickness,
        calibration.ppmm
      );

      for (const line of examState.multiLines) {
        // マーク済みの線分は薄い色、未マークは通常色
        const lineColor = line.marked ? MARKED_LINE_COLOR : CANVAS_CONFIG.LINE_COLOR;
        drawLine(
          ctx,
          line.x1Px,
          line.y1Px,
          line.x2Px,
          line.y2Px,
          lineThicknessPx,
          lineColor
        );

        // マーク済みの場合はマーカーも描画
        if (line.marked && line.markedXPx !== null && line.markedYPx !== null) {
          drawMarker(
            ctx,
            line.markedXPx,
            line.markedYPx,
            markLengthPx,
            CANVAS_CONFIG.MARK_THICKNESS,
            CANVAS_CONFIG.MARK_COLOR
          );
        }
      }
    } else {
      // 単一線分モード
      if (examState.currentLine) {
        const calibration = getCalibration();
        const lineThicknessPx = mmToPx(
          examState.settings.lineThickness,
          calibration.ppmm
        );
        const { x1Px, y1Px, x2Px, y2Px } = examState.currentLine;
        drawLine(
          ctx,
          x1Px,
          y1Px,
          x2Px,
          y2Px,
          lineThicknessPx,
          CANVAS_CONFIG.LINE_COLOR
        );
      }

      // マーク位置を描画
      if (markedPosition) {
        const calibration = getCalibration();
        const markLengthPx = mmToPx(15, calibration.ppmm);

        drawMarker(
          ctx,
          markedPosition.x,
          markedPosition.y,
          markLengthPx,
          CANVAS_CONFIG.MARK_THICKNESS,
          CANVAS_CONFIG.MARK_COLOR
        );
      }
    }
  }, [examState.currentLine, examState.multiLines, examState.settings.lineThickness, markedPosition, isMultiLineMode, canvasSize, lineHidden]);

  /**
   * Canvas描画更新
   */
  useEffect(() => {
    renderCanvas();
  }, [renderCanvas]);

  /**
   * ポインタ入力ハンドラ
   *
   * 検査設定の inputMode に応じて、Apple Pencil(pen)・指タッチ(touch)・マウス(mouse)を
   * 区別し、選択された入力デバイス以外からの入力は無視する。
   */
  const handleCanvasInteraction = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      if (examState.phase !== 'running') return;
      if (lineHidden) return;

      // 入力デバイスのフィルタ（検査準備時に選択した入力モードのみ受け付ける）
      const mode = examState.settings.inputMode;
      if (mode === 'pen' && event.pointerType !== 'pen') return;
      if (mode === 'touch' && event.pointerType !== 'touch') return;
      if (mode === 'mouse' && event.pointerType !== 'mouse') return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      // イベントのデフォルト動作を防止
      event.preventDefault();

      // 座標を取得（PointerEvent は MouseEvent を継承しているのでそのまま渡せる）
      const { x, y } = getCanvasCoordinates(event.nativeEvent, canvas);

      if (isMultiLineMode && examState.multiLines) {
        // 複数線分モード: 最も近い未マーク線分を探す
        const linesWithMarked = examState.multiLines.map((line) => ({
          x1Px: line.x1Px,
          y1Px: line.y1Px,
          x2Px: line.x2Px,
          y2Px: line.y2Px,
          marked: line.marked,
        }));

        const nearestIndex = findNearestLine(x, y, linesWithMarked);
        if (nearestIndex >= 0) {
          recordMultiLineMark(nearestIndex, x, y);
        }
      } else {
        // 単一線分モード
        setMarkedPosition({ x, y });
        recordMark(x, y);
      }
    },
    [
      examState.phase,
      examState.multiLines,
      examState.settings.inputMode,
      lineHidden,
      recordMark,
      recordMultiLineMark,
      isMultiLineMode,
    ]
  );

  /**
   * マーク描画後にCanvasをキャプチャして、最新の試行結果に画像を付与
   *
   * phase が 'waiting' または 'completed' に遷移した直後（マーカー描画済み）にキャプチャする。
   * requestAnimationFrame を使用して、描画が確実に完了した後にキャプチャを実行する。
   */
  useEffect(() => {
    if (
      (examState.phase === 'waiting' || examState.phase === 'completed') &&
      canvasRef.current &&
      examState.trialResults.length > 0
    ) {
      // 描画完了を待ってからキャプチャ
      const rafId = requestAnimationFrame(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const capturedImage = canvas.toDataURL('image/png');

        // 最新の試行結果にキャプチャ画像を付与
        const lastIndex = examState.trialResults.length - 1;
        capturedImagesRef.current.set(lastIndex, capturedImage);
      });

      return () => cancelAnimationFrame(rafId);
    }
  }, [examState.phase, examState.trialResults]);

  /**
   * 次の試行へ進む
   */
  const handleNextTrial = useCallback(() => {
    if (examState.phase !== 'waiting') return;

    // マーク位置をリセット
    setMarkedPosition(null);

    // 次の試行へ
    nextTrial(canvasSize.width, canvasSize.height);
  }, [examState.phase, nextTrial, canvasSize]);

  /**
   * 自動遷移モード:
   *   1. マーク後0.5秒間マーク表示
   *   2. 画面クリア（白画面）→ 指定秒数待機
   *   3. 次の試行の線を表示
   */
  useEffect(() => {
    if (
      examState.phase === 'waiting' &&
      examState.settings.autoAdvance &&
      !isCompleted
    ) {
      const blankSeconds = examState.settings.autoAdvanceSeconds ?? 1;

      // 0.5秒後にマークと線を消す
      const hideTimer = setTimeout(() => {
        setMarkedPosition(null);
        setLineHidden(true);
      }, 500);

      // 0.5秒 + 指定秒数 後に次の試行へ
      const advanceTimer = setTimeout(() => {
        setLineHidden(false);
        nextTrial(canvasSize.width, canvasSize.height);
      }, 500 + blankSeconds * 1000);

      return () => {
        clearTimeout(hideTimer);
        clearTimeout(advanceTimer);
      };
    }
  }, [examState.phase, examState.settings.autoAdvance, examState.settings.autoAdvanceSeconds, isCompleted, nextTrial, canvasSize]);

  /**
   * 検査完了時の処理
   */
  useEffect(() => {
    if (isCompleted) {
      // 結果ページへ遷移
      const summary = getResultSummary();
      const trialResultsForSave = examState.trialResults.map((trial, index) => ({
        ...trial,
        capturedImage: trial.capturedImage ?? capturedImagesRef.current.get(index),
      }));

      // 最終試行の画像が未設定なら、保存直前に現在キャンバスから補完する
      const canvas = canvasRef.current;
      if (canvas && trialResultsForSave.length > 0) {
        const lastIndex = trialResultsForSave.length - 1;
        const lastTrial = trialResultsForSave[lastIndex];
        if (lastTrial && !lastTrial.capturedImage) {
          trialResultsForSave[lastIndex] = {
            ...lastTrial,
            capturedImage: canvas.toDataURL('image/png'),
          };
        }
      }

      // 検査結果を保存
      sessionStorage.setItem(
        'examResult',
        JSON.stringify({
          patient: examState.patient,
          settings: examState.settings,
          trialResults: trialResultsForSave,
          summary,
        })
      );

      // 少し遅延して結果画面へ
      const timer = setTimeout(() => {
        navigate('/exam/result');
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [
    isCompleted,
    examState.patient,
    examState.settings,
    examState.trialResults,
    getResultSummary,
    navigate,
  ]);

  /**
   * キャンセル確認
   */
  const handleCancelConfirm = () => {
    setShowConfirmCancel(true);
  };

  /**
   * キャンセル実行
   */
  const handleCancelExam = () => {
    cancelExam();
    navigate('/exam/prepare');
  };

  /**
   * キャンセルダイアログを閉じる
   */
  const handleCloseCancelDialog = () => {
    setShowConfirmCancel(false);
  };

  // 準備中の場合
  if (!isInitialized || examState.phase === 'idle') {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center">
        <div className="text-gray-600 text-lg">準備中...</div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 bg-white flex flex-col touch-none select-none"
    >
      {/* 進捗バー（設定で表示ON の場合） */}
      {examState.settings.showProgressBar && (
        <div className="absolute top-0 left-0 right-0 h-2 bg-gray-200 z-10">
          <div
            className="h-full bg-blue-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* キャンセルボタン */}
      <button
        onClick={handleCancelConfirm}
        className="absolute top-4 right-4 z-20 p-2 text-gray-400 hover:text-gray-600"
        aria-label="検査をキャンセル"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      {/* 試行番号表示 */}
      <div className="absolute top-4 left-4 z-20 text-gray-500 text-sm">
        <div>{examState.currentTrialNumber} / {examState.settings.trialCount}</div>
        {isMultiLineMode && examState.multiLines && examState.phase === 'running' && (
          <div className="mt-1 text-xs">
            残り {examState.multiLines.filter(l => !l.marked).length} / {examState.multiLines.length} 本
          </div>
        )}
      </div>

      {/* 入力モードヒント（ペン/マウス指定時のみ） */}
      {examState.settings.inputMode === 'pen' && (
        <div className="absolute top-4 right-16 z-20 text-gray-400 text-xs">
          Apple Pencilで操作してください
        </div>
      )}
      {examState.settings.inputMode === 'mouse' && (
        <div className="absolute top-4 right-16 z-20 text-gray-400 text-xs">
          マウスで操作してください
        </div>
      )}

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        className="flex-1 cursor-crosshair"
        style={{ touchAction: 'none' }}
        onPointerDown={handleCanvasInteraction}
      />

      {/* 次へボタン（マーク後に表示、自動遷移モードでは非表示） */}
      {examState.phase === 'waiting' && !examState.settings.autoAdvance && (
        <div className="absolute bottom-8 left-0 right-0 flex justify-center z-20">
          <button
            onClick={handleNextTrial}
            className="bg-blue-600 text-white px-12 py-4 rounded-lg font-bold text-xl shadow-lg hover:bg-blue-700 transition-colors"
          >
            次へ
          </button>
        </div>
      )}

      {/* 完了メッセージ */}
      {isCompleted && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30">
          <div className="bg-white rounded-lg p-8 text-center">
            <div className="text-green-500 mb-4">
              <svg
                className="w-16 h-16 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-800">検査完了</h2>
            <p className="text-gray-600 mt-2">結果を表示しています...</p>
          </div>
        </div>
      )}

      {/* キャンセル確認ダイアログ */}
      {showConfirmCancel && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              検査を中断しますか?
            </h3>
            <p className="text-gray-600 mb-6">
              現在の進捗は保存されません。
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCloseCancelDialog}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                続ける
              </button>
              <button
                onClick={handleCancelExam}
                className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700"
              >
                中断する
              </button>
            </div>
          </div>
        </div>
      )}

      {/* キャリブレーション警告（初回のみ） */}
      {!isCalibrated && examState.phase === 'running' && (
        <div className="absolute top-12 left-4 right-4 z-10">
          <div className="bg-amber-100 border border-amber-300 rounded-lg px-4 py-2 text-amber-800 text-sm text-center">
            キャリブレーション未実施のため、測定精度が低下する可能性があります
          </div>
        </div>
      )}
    </div>
  );
}
